import { useCallback } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useFormsStore } from 'src/features/form/store/useFormsStore';
import { FileUploadType, FormField, FormResponseType } from 'src/features/form/types/form';
import { v4 as uuid } from 'uuid';

import { Field } from './form-field';

export const FieldList = ({ formId }: { formId: string }) => {
  const formData = useFormsStore(
    useCallback(
      (s) => {
        if (formId) {
          return s.forms[formId];
        }
      },
      [formId]
    )
  );
  const onAddNewFormField = useFormsStore((s) => s.onAddNewFormField);
  const onRemoveFormField = useFormsStore((s) => s.onRemoveFormField);
  const onChangeFormField = useFormsStore((s) => s.onChangeFormField);
  const onChangeMultipleFormField = useFormsStore((s) => s.onChangeMultipleFormField);
  const onMoveFormField = useFormsStore((s) => s.onMoveFormField);
  const onChangeFormFieldOptions = useFormsStore((s) => s.onChangeFormFieldOptions);
  const onDeleteFormFieldOptions = useFormsStore((s) => s.onDeleteFormFieldOptions);
  const handleDragNDropField = (dragInfo) => {
    const { destination, source } = dragInfo;
    onMoveFormField(formId, source.index, destination.index);
  };

  const handleChangeResponseType = (index: number, type?: FormResponseType) => {
    if (type === 'star-rating') {
      onChangeMultipleFormField(
        {
          responseType: type,
          ratingStyle: 'star',
          higherRange: 5,
        },
        formId,
        index
      );
    } else if (type === 'linear-scale') {
      const fieldProperties = { responseType: type, lowerRange: 0, higherRange: 10 };
      onChangeMultipleFormField(fieldProperties, formId, index);
    } else if (type === 'radio-box' || type === 'checkbox' || type === 'dropdown') {
      onChangeMultipleFormField(
        {
          responseType: type,
        },
        formId,
        index
      );
      onChangeFormFieldOptions([{ label: '', value: '', optionId: uuid() }], formId, index);
    } else onChangeFormField('responseType', type, formId, index);
  };

  return (
    <div>
      <DragDropContext onDragEnd={handleDragNDropField}>
        <Droppable droppableId="form-fields">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {formData?.fields?.map((field, index) => (
                <Draggable draggableId={`${index}-${field._id}`} key={field._id} index={index}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <Field
                        onAddNewField={() => onAddNewFormField(formId, index + 1)}
                        onDeleteField={() => onRemoveFormField(formId, index)}
                        onDescriptionChange={(description) =>
                          onChangeFormField('description', description, formId, index)
                        }
                        onAddOptions={(options) => {
                          onChangeFormFieldOptions(options, formId, index);
                        }}
                        onDeleteOption={(option) => {
                          onDeleteFormFieldOptions(option, formId, index);
                        }}
                        onAddImage={(image) => {
                          if (image) {
                            onChangeMultipleFormField(
                              {
                                image: image,
                                showImage: true,
                              },
                              formId,
                              index
                            );
                          } else {
                            onChangeFormField('showImage', false, formId, index);
                          }
                        }}
                        onReorderFormFieldOptions={(options) => {
                          onChangeFormFieldOptions(options, formId, index);
                        }}
                        onTitleChange={(title) => onChangeFormField('title', title, formId, index)}
                        onTypeChange={(type) => handleChangeResponseType(index, type)}
                        onRequiredChange={(required) =>
                          onChangeFormField('isRequired', required, formId, index)
                        }
                        onRandomizeOptions={(randomize) =>
                          onChangeFormField('randomiseOptions', randomize, formId, index)
                        }
                        onPlaceholderChange={(placeholder) =>
                          onChangeFormField('placeholder', placeholder, formId, index)
                        }
                        onChangeRatingStyle={(value) =>
                          onChangeFormField('ratingStyle', value, formId, index)
                        }
                        onChangeRange={(value, type) =>
                          onChangeFormField(
                            `${type === 'lowest' ? 'lowerRange' : 'higherRange'}`,
                            value,
                            formId,
                            index
                          )
                        }
                        onChangeRangeLabels={(value, type) =>
                          onChangeFormField(
                            `${type === 'lowest' ? 'lowerRangeLabel' : 'higherRangeLabel'}`,
                            value,
                            formId,
                            index
                          )
                        }
                        onChangeDateRange={(dateRange: [Date, Date]) => {
                          onChangeFormField(`dateRange`, dateRange, formId, index);
                        }}
                        onChangeMaxFiles={(count: number) => {
                          onChangeFormField(`maxFilesCount`, count, formId, index);
                        }}
                        onChangeMaxFileSize={(size: number) => {
                          onChangeFormField(
                            `maxFileSize`,
                            size,
                            formId,
                            index,
                            !size || size > 1000 || size < 0
                          );
                        }}
                        onChangeFileTypes={(types: FileUploadType[]) => {
                          onChangeFormField(`fileTypes`, types, formId, index);
                        }}
                        data={field}
                        formData={formData}
                        index={index}
                        dragHandleProps={provided.dragHandleProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
